import { isObject, isString } from 'lodash'

import parseBillingDraft from '~/features/billing-composer/parseBillingDraft'
import type { BillingDraft } from '~/features/billing-composer/types'

import type { AssistantClarificationQuestion, AssistantDashboardPanel } from './streamTypes'

/**
 * Parser for the persisted per-message tool trace (see syn-ops `AssistantService.serializeToolTrace`):
 * a JSON array of `{ tool, params?, result?, data? }` entries. Tool inputs are stored in full, which lets
 * read-only surfaces (the shared-conversation page) re-render dashboard panels and clarification cards
 * without any live stream.
 */

export type ToolTraceEntry = {
  tool: string
  params?: Record<string, unknown>
  result?: string
  data?: string
}

export function parseToolTrace(toolTrace: string | undefined | null): ToolTraceEntry[] {
  if (!toolTrace) return []
  try {
    const parsed: unknown = JSON.parse(toolTrace)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isTraceEntry)
  } catch {
    return []
  }
}

function isTraceEntry(value: unknown): value is ToolTraceEntry {
  return isObject(value) && isString((value as { tool?: unknown }).tool)
}

/**
 * Rebuilds dashboard panels from the trace. The live turn renders a `submit_dashboard_layout` skeleton first, then
 * fills each tile with `submit_dashboard_panel` (carrying its tileId). Replay mirrors that: tiles are laid out in their
 * declared order and filled panels overlay them by tileId; any filled panel without a matching tile is appended.
 */
export function panelsFromTrace(entries: ToolTraceEntry[], messageId: string): AssistantDashboardPanel[] {
  const layoutTiles = layoutTilesFromTrace(entries)
  const fills = entries
    .filter((entry) => entry.tool === 'submit_dashboard_panel' && isObject(entry.params))
    .map((entry, index) => panelFromFill(entry.params as Record<string, unknown>, `${messageId}-panel-${index}`))

  if (layoutTiles.length === 0) {
    return fills
  }

  const fillByTile = new Map(fills.filter((panel) => isString(panel.tileId)).map((panel) => [panel.tileId, panel]))
  const used = new Set<string>()
  const ordered = layoutTiles.map((tile) => {
    const fill = fillByTile.get(tile.tileId)
    if (fill) {
      used.add(tile.tileId)
      return fill
    }
    return {
      panelId: tile.tileId,
      tileId: tile.tileId,
      panelType: tile.panelType,
      title: tile.title,
      panelConfig: {},
      status: 'ready' as const
    }
  })
  const appended = fills.filter((panel) => !panel.tileId || !used.has(panel.tileId))
  return [...ordered, ...appended]
}

function panelFromFill(params: Record<string, unknown>, fallbackId: string): AssistantDashboardPanel {
  const tileId = isString(params.tileId) ? params.tileId : undefined
  return {
    panelId: tileId ?? fallbackId,
    tileId,
    panelType: isString(params.panelType) ? params.panelType : 'narrative',
    title: isString(params.title) ? params.title : undefined,
    panelConfig: isObject(params.panelConfig) ? (params.panelConfig as Record<string, unknown>) : {},
    summaryText: isString(params.summaryText) ? params.summaryText : undefined,
    provenance: params.provenance,
    status: 'ready'
  }
}

type LayoutTile = { tileId: string; panelType: string; title?: string }

function layoutTilesFromTrace(entries: ToolTraceEntry[]): LayoutTile[] {
  const entry = entries.find((item) => item.tool === 'submit_dashboard_layout' && isObject(item.params))
  const tiles = entry?.params?.tiles
  if (!Array.isArray(tiles)) return []
  return tiles
    .filter(isObject)
    .map((tile) => tile as Record<string, unknown>)
    .filter((tile) => isString(tile.tileId))
    .map((tile) => ({
      tileId: tile.tileId as string,
      panelType: isString(tile.panelType) ? tile.panelType : 'narrative',
      title: isString(tile.title) ? tile.title : undefined
    }))
}

/** Rebuilds the clarification questions asked via `request_clarification`, if any. */
export function clarificationFromTrace(
  entries: ToolTraceEntry[]
): { intro?: string; questions: AssistantClarificationQuestion[] } | null {
  const entry = entries.find((item) => item.tool === 'request_clarification' && isObject(item.params))
  if (!entry?.params) return null
  const questions = Array.isArray(entry.params.questions)
    ? (entry.params.questions as AssistantClarificationQuestion[]).filter(
        (question) => isObject(question) && isString(question.id) && isString(question.prompt)
      )
    : []
  if (questions.length === 0) return null
  return {
    intro: isString(entry.params.intro) ? entry.params.intro : undefined,
    questions
  }
}

/**
 * Rebuilds the billing drafts proposed via `submit_billing_draft`, in trace order. The tool input is the
 * draft payload itself (same shape as the live `billing_draft` event), so resume/history can re-render
 * the Draft cards without a stream.
 */
export function billingDraftsFromTrace(entries: ToolTraceEntry[], messageId: string): BillingDraft[] {
  return entries
    .filter((entry) => entry.tool === 'submit_billing_draft' && isObject(entry.params))
    .map((entry, index) => parseBillingDraft(entry.params, `${messageId}-draft-${index}`))
    .filter((draft): draft is BillingDraft => draft !== null)
}

/** Trace entries worth showing as breadcrumb chips (panels, clarifications and drafts render as rich blocks instead). */
const RICH_BLOCK_TOOLS = new Set([
  'submit_dashboard_layout',
  'submit_dashboard_panel',
  'request_clarification',
  'submit_billing_draft'
])

export function breadcrumbsFromTrace(entries: ToolTraceEntry[]): ToolTraceEntry[] {
  return entries.filter((entry) => !RICH_BLOCK_TOOLS.has(entry.tool))
}
